import React from 'react';
import { Box, Link, SpaceBetween } from '@amzn/awsui-components-console';

/**
 * Custom Markdown Renderer for chatbot responses
 * Handles common markdown syntax without external dependencies
 */
export default function MarkdownRenderer({ content }) {
    if (!content) return null;

    const parseMarkdown = (text) => {
        const lines = text.split('\n');
        const elements = [];
        let currentList = [];
        let currentListIndent = 0;
        let inCodeBlock = false;
        let codeBlockContent = [];
        let codeBlockLanguage = '';

        const flushList = () => {
            if (currentList.length > 0) {
                elements.push({
                    type: 'list',
                    items: [...currentList],
                    key: `list-${elements.length}`,
                });
                currentList = [];
                currentListIndent = 0;
            }
        };

        const flushCodeBlock = () => {
            if (codeBlockContent.length > 0) {
                elements.push({
                    type: 'codeblock',
                    content: codeBlockContent.join('\n'),
                    language: codeBlockLanguage,
                    key: `code-${elements.length}`,
                });
                codeBlockContent = [];
                codeBlockLanguage = '';
            }
        };

        for (let i = 0; i < lines.length; i++) {
            const line = lines[i];

            // Handle code blocks
            if (line.trim().startsWith('```')) {
                if (inCodeBlock) {
                    // End code block
                    inCodeBlock = false;
                    flushCodeBlock();
                } else {
                    // Start code block
                    flushList();
                    inCodeBlock = true;
                    codeBlockLanguage = line.trim().replace('```', '');
                }
                continue;
            }

            if (inCodeBlock) {
                codeBlockContent.push(line);
                continue;
            }

            // Handle headers
            if (line.startsWith('###')) {
                flushList();
                elements.push({
                    type: 'h3',
                    content: line.replace('### ', ''),
                    key: `h3-${elements.length}`,
                });
                continue;
            }

            if (line.startsWith('##')) {
                flushList();
                elements.push({
                    type: 'h2',
                    content: line.replace('## ', ''),
                    key: `h2-${elements.length}`,
                });
                continue;
            }

            if (line.startsWith('#')) {
                flushList();
                elements.push({
                    type: 'h1',
                    content: line.replace('# ', ''),
                    key: `h1-${elements.length}`,
                });
                continue;
            }

            // Handle numbered lists
            const numberedMatch = line.match(/^(\s*)(\d+)\.\s+(.+)$/);
            if (numberedMatch) {
                const [, indent, number, content] = numberedMatch;
                const indentLevel = indent.length;

                if (currentList.length === 0 || indentLevel >= currentListIndent) {
                    currentListIndent = indentLevel;
                    currentList.push({
                        content: parseInlineFormatting(content),
                        type: 'numbered',
                        indent: indentLevel,
                    });
                } else {
                    flushList();
                    currentListIndent = indentLevel;
                    currentList.push({
                        content: parseInlineFormatting(content),
                        type: 'numbered',
                        indent: indentLevel,
                    });
                }
                continue;
            }

            // Handle bullet lists
            const bulletMatch = line.match(/^(\s*)[-*]\s+(.+)$/);
            if (bulletMatch) {
                const [, indent, content] = bulletMatch;
                const indentLevel = indent.length;

                if (currentList.length === 0 || indentLevel >= currentListIndent) {
                    currentListIndent = indentLevel;
                    currentList.push({
                        content: parseInlineFormatting(content),
                        type: 'bullet',
                        indent: indentLevel,
                    });
                } else {
                    flushList();
                    currentListIndent = indentLevel;
                    currentList.push({
                        content: parseInlineFormatting(content),
                        type: 'bullet',
                        indent: indentLevel,
                    });
                }
                continue;
            }

            // Handle empty lines
            if (line.trim() === '') {
                flushList();
                if (elements.length > 0 && elements[elements.length - 1].type !== 'break') {
                    elements.push({
                        type: 'break',
                        key: `break-${elements.length}`,
                    });
                }
                continue;
            }

            // Handle regular paragraphs
            if (line.trim()) {
                flushList();
                elements.push({
                    type: 'paragraph',
                    content: parseInlineFormatting(line),
                    key: `p-${elements.length}`,
                });
            }
        }

        // Flush any remaining content
        flushList();
        if (inCodeBlock) {
            flushCodeBlock();
        }

        return elements;
    };

    const parseInlineFormatting = (text) => {
        // Handle inline code first (before other formatting)
        const codeRegex = /`([^`]+)`/g;
        const parts = [];
        let lastIndex = 0;
        let match;

        while ((match = codeRegex.exec(text)) !== null) {
            // Add text before the code
            if (match.index > lastIndex) {
                const beforeText = text.substring(lastIndex, match.index);
                parts.push(...parseOtherFormatting(beforeText));
            }

            // Add the code span
            parts.push({
                type: 'code',
                content: match[1],
                key: `code-${parts.length}`,
            });

            lastIndex = match.index + match[0].length;
        }

        // Add remaining text
        if (lastIndex < text.length) {
            const remainingText = text.substring(lastIndex);
            parts.push(...parseOtherFormatting(remainingText));
        }

        return parts.length > 0 ? parts : parseOtherFormatting(text);
    };

    const parseOtherFormatting = (text) => {
        const parts = [];
        let currentText = text;

        // Handle bold (**text**)
        currentText = currentText.replace(/\*\*([^*]+)\*\*/g, (match, content) => {
            const placeholder = `__BOLD_${parts.length}__`;
            parts.push({
                type: 'bold',
                content: content,
                key: `bold-${parts.length}`,
            });
            return placeholder;
        });

        // Handle italic (*text*)
        currentText = currentText.replace(/\*([^*]+)\*/g, (match, content) => {
            const placeholder = `__ITALIC_${parts.length}__`;
            parts.push({
                type: 'italic',
                content: content,
                key: `italic-${parts.length}`,
            });
            return placeholder;
        });

        // Handle links [text](url)
        currentText = currentText.replace(/\[([^\]]+)\]\(([^)]+)\)/g, (match, text, url) => {
            const placeholder = `__LINK_${parts.length}__`;
            parts.push({
                type: 'link',
                text: text,
                url: url,
                key: `link-${parts.length}`,
            });
            return placeholder;
        });

        // Split by placeholders and create final array
        const finalParts = [];
        const placeholderRegex = /__([A-Z]+)_(\d+)__/g;
        let lastIndex = 0;
        let match;

        while ((match = placeholderRegex.exec(currentText)) !== null) {
            // Add text before placeholder
            if (match.index > lastIndex) {
                const textBefore = currentText.substring(lastIndex, match.index);
                if (textBefore) {
                    finalParts.push({
                        type: 'text',
                        content: textBefore,
                        key: `text-${finalParts.length}`,
                    });
                }
            }

            // Add the formatted part
            const partIndex = parseInt(match[2]);
            if (parts[partIndex]) {
                finalParts.push(parts[partIndex]);
            }

            lastIndex = match.index + match[0].length;
        }

        // Add remaining text
        if (lastIndex < currentText.length) {
            const remainingText = currentText.substring(lastIndex);
            if (remainingText) {
                finalParts.push({
                    type: 'text',
                    content: remainingText,
                    key: `text-${finalParts.length}`,
                });
            }
        }

        return finalParts.length > 0
            ? finalParts
            : [
                  {
                      type: 'text',
                      content: text,
                      key: 'text-0',
                  },
              ];
    };

    const renderInlineContent = (parts) => {
        if (typeof parts === 'string') {
            return parts;
        }

        return parts.map((part) => {
            switch (part.type) {
                case 'bold':
                    return <strong key={part.key}>{part.content}</strong>;
                case 'italic':
                    return <em key={part.key}>{part.content}</em>;
                case 'code':
                    return (
                        <code
                            key={part.key}
                            style={{
                                backgroundColor: '#f3f4f6',
                                padding: '2px 4px',
                                borderRadius: '3px',
                                fontFamily: 'monospace',
                                fontSize: '0.9em',
                                color: '#374151',
                            }}
                        >
                            {part.content}
                        </code>
                    );
                case 'link':
                    return (
                        <Link key={part.key} href={part.url} external variant="primary">
                            {part.text}
                        </Link>
                    );
                case 'text':
                default:
                    return part.content;
            }
        });
    };

    const elements = parseMarkdown(content);

    return (
        <SpaceBetween size="xs">
            {elements.map((element) => {
                switch (element.type) {
                    case 'h1':
                        return (
                            <Box key={element.key} margin={{ top: 's', bottom: 'xs' }}>
                                <h1 style={{ fontSize: '1.5em', fontWeight: 'bold', margin: 0 }}>{element.content}</h1>
                            </Box>
                        );

                    case 'h2':
                        return (
                            <Box key={element.key} margin={{ top: 's', bottom: 'xs' }}>
                                <h2 style={{ fontSize: '1.3em', fontWeight: 'bold', margin: 0 }}>{element.content}</h2>
                            </Box>
                        );

                    case 'h3':
                        return (
                            <Box key={element.key} margin={{ top: 's', bottom: 'xs' }}>
                                <h3 style={{ fontSize: '1.1em', fontWeight: 'bold', margin: 0 }}>{element.content}</h3>
                            </Box>
                        );

                    case 'paragraph':
                        return (
                            <Box key={element.key} margin={{ bottom: 'xs' }}>
                                {renderInlineContent(element.content)}
                            </Box>
                        );

                    case 'list':
                        return (
                            <Box key={element.key} margin={{ bottom: 'xs', left: 's' }}>
                                <ul style={{ margin: 0, paddingLeft: '1.2em' }}>
                                    {element.items.map((item, idx) => (
                                        <li
                                            key={`${element.key}-item-${idx}`}
                                            style={{
                                                marginBottom: '4px',
                                                marginLeft: `${item.indent * 0.5}em`,
                                            }}
                                        >
                                            {renderInlineContent(item.content)}
                                        </li>
                                    ))}
                                </ul>
                            </Box>
                        );

                    case 'codeblock':
                        return (
                            <Box
                                key={element.key}
                                margin={{ bottom: 's' }}
                                padding="s"
                                backgroundColor="color-background-code-editor"
                                style={{
                                    borderRadius: '6px',
                                    border: '1px solid #e5e7eb',
                                    backgroundColor: '#f8f9fa',
                                }}
                            >
                                <pre
                                    style={{
                                        margin: 0,
                                        fontFamily: 'monospace',
                                        fontSize: '0.9em',
                                        whiteSpace: 'pre-wrap',
                                        overflowX: 'auto',
                                        color: '#374151',
                                    }}
                                >
                                    {element.content}
                                </pre>
                            </Box>
                        );

                    case 'break':
                        return <div key={element.key} style={{ height: '8px' }} />;

                    default:
                        return null;
                }
            })}
        </SpaceBetween>
    );
}
